import baseAPI from '@/api/base';
import {
  EditOrganization,
  GetOrganization,
  RegisterOrganization,
  SetOrganizationArchived,
} from '@/dtos/organization.dto';
import {
  ARCHIVE_ORGANIZATION_URL,
  DELETE_ORGANIZATION_URL,
  EDIT_ORGANIZATION_URL,
  GET_ORGANIZATION_STATS_URL,
  GET_ORGANIZATION_URL,
  REGISTER_ORGANIZATION_URL,
  SET_ORGANIZATION_APPS_URL,
  SIGNUP_ORGANIZATION_URL,
  TOGGLE_ORGANIZATION_IS_ACTIVE_URL,
} from '@/utils/urls';
import { AxiosResponse } from 'axios';

// Deleting answers wrong, expired or too-soon codes (and an email that couldn't
// go out) with these statuses; they come back as answers for the dialog to
// show, not as errors turned into toasts.
const DELETION_ANSWER_STATUSES = [400, 404, 429, 502];
const acceptDeletionAnswers = (status: number) =>
  (status >= 200 && status < 300) || DELETION_ANSWER_STATUSES.includes(status);

class Organization {
  static getOrganizations = async (
    getOrganization: GetOrganization
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_ORGANIZATION_URL, {
      params: getOrganization,
    });
    console.log('response', response);
    return response;
  };

  static getOrganizationStats = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_ORGANIZATION_STATS_URL);
    return response;
  };

  static registerOrganization = async (
    formData: FormData
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(
      REGISTER_ORGANIZATION_URL,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    console.log('response', response);
    return response;
  };

  static signupOrganization = async (
    registerOrganization: RegisterOrganization
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(
      SIGNUP_ORGANIZATION_URL,
      {
        ...registerOrganization,
      }
    );
    console.log('response', response);
    return response;
  };

  static editOrganization = async (
    formData: FormData
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(
      EDIT_ORGANIZATION_URL,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response;
  };

  static toggleOrganizationIsActive = async (
    editOrganization: EditOrganization
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.patch(
      TOGGLE_ORGANIZATION_IS_ACTIVE_URL,
      {
        ...editOrganization,
      }
    );
    return response;
  };

  // Active or disabled — set, not flipped, so a stale page can't undo itself.
  static setOrganizationStatus = async (id: string, isActive: boolean): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.patch(TOGGLE_ORGANIZATION_IS_ACTIVE_URL, { id, isActive });
    return response;
  };

  // Which apps the organization's people can sign in to: 'screenkode',
  // 'chabad_digital' or both.
  static setOrganizationApps = async (id: string, apps: string[]): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.patch(SET_ORGANIZATION_APPS_URL, { id, apps });
    return response;
  };

  static setOrganizationArchived = async (
    payload: SetOrganizationArchived
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.patch(ARCHIVE_ORGANIZATION_URL, payload);
    return response;
  };

  // Emails the signed-in admin the code that deleting the organization takes.
  static requestOrganizationDeletionCode = async (id: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(
      `${DELETE_ORGANIZATION_URL}/${id}/deletion-code`,
      {},
      { validateStatus: acceptDeletionAnswers }
    );
    return response;
  };

  static deleteOrganization = async (id: string, code: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.delete(`${DELETE_ORGANIZATION_URL}/${id}`, {
      data: { code },
      validateStatus: acceptDeletionAnswers,
    });
    return response;
  };
}

export default Organization;
